import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import { getList } from "../../api/generics";
import { getRoutePath } from "../../routes";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CCollapse,
  CDataTable,
  CLabel,
  CPagination,
  CRow,
  CSelect,
} from "@coreui/react";

import moment from "moment";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";

import { BsFillPlusCircleFill } from "react-icons/bs";
import ReceivedDocumentDeleteModal from "./ReceivedDocumentDeleteModal";
import ReceivedDocumentDetailModal from "./ReceivedDocumentDetailModal";
import ReceivedDocumentCrudActions from "./ReceivedDocumentCrudActions";
import { ReceivedDocumentMobileCard } from "./ReceivedDocumentMobileCard";
import { useIsMobile } from "../../hooks/is-mobile";
import { formatToCurrency } from "../../currency/format";
import { formatUTCDateTime } from "../../utils/dates";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import {
  RECEIVED_DOCUMENTS_CREATE,
  RECEIVED_DOCUMENTS_RETRIEVE,
} from "../../auth/permissions";

import {
  ReceivedDocument,
  DOCUMENT_TYPE_CHOICES,
  DOCUMENT_TYPE_LABELS,
} from "../../models/received-document";
import SystemNotificationBanners from "../notifications/SystemNotificationBanners";

const ITEMS_PER_PAGE = 20;

const ReceivedDocumentCrud = () => {
  const fields = [
    {
      key: "documentType",
      _classes: ["text-center"],
      label: "Tipo",
    },
    {
      key: "documentNumber",
      _classes: ["text-center"],
      label: "N° Documento",
    },
    { key: "documentDate", _classes: ["text-center"], label: "Fecha" },
    {
      key: "supplierInfo",
      _classes: ["text-center"],
      label: "Emisor (RUC - Razón Social)",
    },
    { key: "amount", _classes: ["text-center"], label: "Total" },
    { key: "iva10Percent", _classes: ["text-center"], label: "IVA 10%" },
    { key: "iva5Percent", _classes: ["text-center"], label: "IVA 5%" },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<ReceivedDocument[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [documentTypeFilter, setDocumentTypeFilter] = useState<string>("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState<
    ReceivedDocument | undefined
  >(undefined);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewingDocument, setViewingDocument] = useState<
    ReceivedDocument | undefined
  >(undefined);

  const [filterCollapse, setFilterCollapse] = useState(false);
  const toggleFilterCollapse = (e: React.ChangeEvent<HTMLButtonElement>) => {
    setFilterCollapse(!filterCollapse);
    e.preventDefault();
  };

  const history = useHistory();

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;
    if (page <= 0) page = 1;

    let startDateStr = urlParams.get("start_date") ?? null;
    let startDate: Date | null = null;
    if (startDateStr) startDate = new Date(startDateStr);

    let endDateStr = urlParams.get("end_date") ?? null;
    let endDate: Date | null = null;
    if (endDateStr) endDate = new Date(endDateStr);

    let documentType = urlParams.get("document_type") ?? "";

    return { page, startDate, endDate, documentType };
  };

  const fetchDocuments = useCallback(async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * (urlParams.page - 1);
    const additionalParams = new Map<string, string | string[]>();
    additionalParams.set("tax_payer_id", taxPayer.id!.toString());

    if (urlParams.startDate !== null) {
      additionalParams.set("start_date", urlParams.startDate.toISOString());
    }
    if (urlParams.endDate !== null) {
      additionalParams.set("end_date", urlParams.endDate.toISOString());
    }
    if (urlParams.documentType) {
      additionalParams.set("document_type", urlParams.documentType);
    }

    setStartDate(urlParams.startDate);
    setEndDate(urlParams.endDate);
    setDocumentTypeFilter(urlParams.documentType);

    const state = await getList<ReceivedDocument>(
      "/received_documents/",
      limit,
      offset,
      additionalParams,
    );

    if (state.status === SUCCESS) {
      if (state.data !== undefined) {
        const count = state.data.count;
        const pages = Math.ceil((count ?? 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setDocuments(state.data.items);
        setCurrentPage(urlParams.page);
      }
    } else {
      const message = state.detail ?? "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("page", page.toString());
    history.push(`?${urlParams.toString()}`);
  };

  const onAddClick = () => {
    history.push(getRoutePath("addReceivedDocument"));
  };

  const onReadClick = (document: ReceivedDocument) => {
    setViewingDocument(document);
    setShowDetailModal(true);
  };

  const onUpdateClick = (document: ReceivedDocument) => {
    const baseUrl = getRoutePath("addReceivedDocument");
    history.push(`${baseUrl}?id=${document.id!}`);
  };

  const onDeleteClick = (document: ReceivedDocument) => {
    setDeletingDocument(document);
    setShowDeleteModal(true);
  };

  const onDocumentDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const onDocumentDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowDeleteModal(false);
    setDeletingDocument(undefined);
    setLoading(true);
    fetchDocuments();
  };

  const onStartDateChange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) return;
    setStartDate(new Date((value as moment.Moment).toISOString()));
  };

  const onEndDateChange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) return;
    setEndDate(new Date((value as moment.Moment).toISOString()));
  };

  const onDocumentTypeFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setDocumentTypeFilter(e.target.value);
  };

  const onSearch = useCallback(() => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("page", "1");

    if (startDate !== null) {
      urlParams.set("start_date", startDate.toISOString());
    } else {
      urlParams.delete("start_date");
    }

    if (endDate !== null) {
      urlParams.set("end_date", endDate.toISOString());
    } else {
      urlParams.delete("end_date");
    }

    if (documentTypeFilter) {
      urlParams.set("document_type", documentTypeFilter);
    } else {
      urlParams.delete("document_type");
    }

    history.push(`?${urlParams.toString()}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, documentTypeFilter]);

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("receivedDocuments")) return;
      fetchDocuments();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, taxPayer]);

  useEffect(() => {
    setLoading(true);
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2">
                <h3>Documentos Recibidos</h3>
              </div>
              <div className="p-2">
                <PermissionRequiredComponent
                  permissionName={RECEIVED_DOCUMENTS_CREATE}
                >
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Nuevo
                  </CButton>
                </PermissionRequiredComponent>
              </div>
            </CCardHeader>
            <SystemNotificationBanners />
            <CCardBody>
              <CRow>
                <CCol>
                  <CButton onClick={toggleFilterCollapse} className={"pl-0 filter-toggle"}>
                    <h5>
                      <i className="fa fa-filter mr-2"></i>
                      {filterCollapse ? "Ocultar Filtros" : "Ver Filtros"}
                    </h5>
                  </CButton>
                </CCol>
              </CRow>
              <CCollapse show={filterCollapse}>
                <CRow className={"mt-2"}>
                  <CCol md={2}>
                    <CLabel className={"mt-2"}>Tipo:</CLabel>
                  </CCol>
                  <CCol md={4}>
                    <CSelect
                      value={documentTypeFilter}
                      onChange={onDocumentTypeFilterChange}
                    >
                      <option value="">Todos</option>
                      {DOCUMENT_TYPE_CHOICES.map((choice) => (
                        <option key={choice.value} value={choice.value}>
                          {choice.label}
                        </option>
                      ))}
                    </CSelect>
                  </CCol>
                </CRow>
                <CRow className={"mt-2"}>
                  <CCol md={2}>
                    <CLabel className={"mt-2"}>Desde:</CLabel>
                  </CCol>
                  <CCol>
                    <Datetime
                      className="pl-0"
                      onChange={onStartDateChange}
                      value={startDate !== null ? startDate : ""}
                      locale="es/PY"
                      dateFormat="DD/MM/YYYY"
                      timeFormat={false}
                      utc={true}
                      closeOnSelect={true}
                    />
                  </CCol>
                  <CCol md={2}>
                    <CLabel className={"mt-2"}>Hasta:</CLabel>
                  </CCol>
                  <CCol>
                    <Datetime
                      className="pl-0"
                      onChange={onEndDateChange}
                      value={endDate !== null ? endDate : ""}
                      locale="es/PY"
                      dateFormat="DD/MM/YYYY"
                      timeFormat={false}
                      utc={true}
                      closeOnSelect={true}
                    />
                  </CCol>
                </CRow>
                <CRow className="mb-4 mt-2">
                  <CCol>
                    <CButtonGroup>
                      <CButton
                        type="button"
                        color="primary"
                        variant="outline"
                        onClick={onSearch}
                      >
                        Filtrar
                      </CButton>
                    </CButtonGroup>
                  </CCol>
                </CRow>
              </CCollapse>

              {isMobile ? (
                <>
                  {documents.length === 0 ? (
                    <h2 className="text-center">No se encontraron registros</h2>
                  ) : (
                    documents.map((doc) => (
                      <ReceivedDocumentMobileCard
                        key={doc.id}
                        document={doc}
                        onReadClick={onReadClick}
                        onUpdateClick={onUpdateClick}
                        onDeleteClick={onDeleteClick}
                      />
                    ))
                  )}
                </>
              ) : (
                <CDataTable
                  noItemsView={{
                    noItems: "No se encontraron registros",
                    noResults: "No se encontraron registros",
                  }}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={documents}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                  documentType: (item: ReceivedDocument) => (
                    <td
                      className="text-center"
                      style={{ verticalAlign: "middle" }}
                    >
                      {item.documentType
                        ? (DOCUMENT_TYPE_LABELS[item.documentType] ??
                          item.documentType)
                        : "-"}
                    </td>
                  ),
                  documentNumber: (item: ReceivedDocument) => (
                    <td
                      className="text-center"
                      style={{ verticalAlign: "middle" }}
                    >
                      {item.documentNumber ?? "-"}
                    </td>
                  ),
                  documentDate: (item: ReceivedDocument) => (
                    <td
                      className="text-center"
                      style={{ verticalAlign: "middle" }}
                    >
                      {item.documentDate
                        ? formatUTCDateTime(new Date(item.documentDate))
                        : "-"}
                    </td>
                  ),
                  supplierInfo: (item: ReceivedDocument) => (
                    <td
                      className="text-center"
                      style={{ verticalAlign: "middle" }}
                    >
                      {item.supplierRuc && item.supplierSocialName
                        ? `${item.supplierRuc} - ${item.supplierSocialName}`
                        : (item.supplierSocialName ?? "-")}
                    </td>
                  ),
                  amount: (item: ReceivedDocument) => (
                    <td
                      className="text-center"
                      style={{ verticalAlign: "middle" }}
                    >
                      {item.amount !== undefined && item.currency
                        ? formatToCurrency(item.amount, item.currency)
                        : "-"}
                    </td>
                  ),
                  iva10Percent: (item: ReceivedDocument) => (
                    <td
                      className="text-center"
                      style={{ verticalAlign: "middle" }}
                    >
                      {item.iva10Percent !== undefined && item.currency
                        ? formatToCurrency(item.iva10Percent, item.currency)
                        : "-"}
                    </td>
                  ),
                  iva5Percent: (item: ReceivedDocument) => (
                    <td
                      className="text-center"
                      style={{ verticalAlign: "middle" }}
                    >
                      {item.iva5Percent !== undefined && item.currency
                        ? formatToCurrency(item.iva5Percent, item.currency)
                        : "-"}
                    </td>
                  ),
                  actions: (item: ReceivedDocument) => (
                    <PermissionRequiredComponent
                      permissionName={RECEIVED_DOCUMENTS_RETRIEVE}
                    >
                      <ReceivedDocumentCrudActions
                        document={item}
                        onReadClick={onReadClick}
                        onUpdateClick={onUpdateClick}
                        onDeleteClick={onDeleteClick}
                      />
                    </PermissionRequiredComponent>
                  ),
                  }}
                />
              )}
              <div className="d-flex justify-content-center">
                <CPagination
                  pages={totalPages}
                  activePage={currentPage}
                  onActivePageChange={(i: number) => onPageChange(i)}
                  className={totalPages < 2 ? "d-none" : ""}
                />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <ReceivedDocumentDeleteModal
        show={showDeleteModal}
        onCancel={onDocumentDeleteCancel}
        onSuccess={onDocumentDeleteSuccess}
        document={deletingDocument}
      />
      <ReceivedDocumentDetailModal
        show={showDetailModal}
        document={viewingDocument}
        onCancel={() => {
          setShowDetailModal(false);
          setViewingDocument(undefined);
        }}
      />
    </>
  );
};

export default ReceivedDocumentCrud;
